import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateTaskGroupTemplateInput,
  TaskGroupTemplate,
  UpdateTaskGroupTemplateInput,
} from '@this+that/models';

const FIELDS = `
  taskGroupId
  id
  name
  iconKey
  colorHex
  descriptionTemplate
  createdBy
  createdAt
  updatedAt
`;

const GET = gql`
  query taskGroupTemplates($taskGroupId: ID!) {
    taskGroupTemplates(taskGroupId: $taskGroupId) { ${FIELDS} }
  }
`;

const CREATE = gql`
  mutation createTaskGroupTemplate($input: CreateTaskGroupTemplateInput!) {
    createTaskGroupTemplate(input: $input) { ${FIELDS} }
  }
`;

const UPDATE = gql`
  mutation updateTaskGroupTemplate($input: UpdateTaskGroupTemplateInput!) {
    updateTaskGroupTemplate(input: $input) { ${FIELDS} }
  }
`;

const DELETE = gql`
  mutation deleteTaskGroupTemplate($taskGroupId: ID!, $id: ID!) {
    deleteTaskGroupTemplate(taskGroupId: $taskGroupId, id: $id)
  }
`;

interface GetData {
  taskGroupTemplates: TaskGroupTemplate[];
}

export function useGetTaskGroupTemplates() {
  const { loading, refetch } = useQuery<GetData>(GET, { skip: true });

  const getTaskGroupTemplates = useCallback(
    async (taskGroupId: string) => {
      const { data } = await refetch({ taskGroupId });
      return data?.taskGroupTemplates ?? [];
    },
    [refetch],
  );

  return { getTaskGroupTemplates, loading };
}

export function useCreateTaskGroupTemplate() {
  const [mutate, { loading }] = useMutation<{
    createTaskGroupTemplate: TaskGroupTemplate;
  }>(CREATE);

  const createTaskGroupTemplate = useCallback(
    async (input: CreateTaskGroupTemplateInput) =>
      (await mutate({ variables: { input } })).data?.createTaskGroupTemplate ??
      null,
    [mutate],
  );

  return { createTaskGroupTemplate, loading };
}

export function useUpdateTaskGroupTemplate() {
  const [mutate, { loading }] = useMutation<{
    updateTaskGroupTemplate: TaskGroupTemplate;
  }>(UPDATE);

  const updateTaskGroupTemplate = useCallback(
    async (input: UpdateTaskGroupTemplateInput) =>
      (await mutate({ variables: { input } })).data?.updateTaskGroupTemplate ??
      null,
    [mutate],
  );

  return { updateTaskGroupTemplate, loading };
}

export function useDeleteTaskGroupTemplate() {
  const [mutate, { loading }] = useMutation<{
    deleteTaskGroupTemplate: boolean;
  }>(DELETE);

  const deleteTaskGroupTemplate = useCallback(
    async (taskGroupId: string, id: string) =>
      (await mutate({ variables: { taskGroupId, id } })).data
        ?.deleteTaskGroupTemplate ?? false,
    [mutate],
  );

  return { deleteTaskGroupTemplate, loading };
}
